import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  EquipmentFilterStateProps,
  FuelFilterStateProps,
  MerchantLocationFilterStateProps,
} from '@/store/redux/actions/pages/pcp';
import { PCPCombinedState } from '@/store/redux/reducers/pages/pcp';
import { BodyColorsStateProps } from '@/store/redux/reducers/pages/pcp/filters/bodyColors';
import { DriveStateProps } from '@/store/redux/reducers/pages/pcp/filters/drive';
import { EmissionFilterStateProps } from '@/store/redux/reducers/pages/pcp/filters/emission';
import { InteriorFilterStateProps } from '@/store/redux/reducers/pages/pcp/filters/interior';
import { LocationStateProps } from '@/store/redux/reducers/pages/pcp/filters/location';
import { PriceFilterStateProps } from '@/store/redux/reducers/pages/pcp/filters/price';
import { VehicleConditionStateProps } from '@/store/redux/reducers/pages/pcp/filters/vehicleCondition';
import { VehicleTypeFilterStateProps } from '@/store/redux/reducers/pages/pcp/filters/vehicleType';
import { PCPStateProps } from '@/store/redux/reducers/pages/pcp/page';
import { MakeModelFilterData } from '@/store/redux/slices/pages/pcp/filters/makeModel';

import { RouteFilter } from '../types';

export const usePCPReduxState = (defaultPriceRange: PriceFilterStateProps) => {
  const filterMakeModel = useSelector(
    (
      state: any
    ): {
      make: string;
      model: string;
    }[] =>
      state.pages.pcp.filters.makeModel.data.reduce(
        (
          acc: { make: string; model: string }[],
          {
            makes,
            models,
          }: {
            makes: MakeModelFilterData['makes'];
            models: MakeModelFilterData['models'];
          }
        ) => [
          ...acc,
          ...(makes.selected || models.selected
            ? [
                {
                  ...(makes.selected && { make: makes.selected }),
                  ...(models.selected && { model: models.selected }),
                },
              ]
            : []),
        ],
        []
      )
  );

  const filterBodyColors = useSelector(
    (state: any): BodyColorsStateProps['colors'] =>
      (state.pages.pcp as PCPCombinedState).filters.bodyColors.colors
  );

  const filterDrive = useSelector(
    (state: any): DriveStateProps =>
      (state.pages.pcp as PCPCombinedState).filters.drive
  );

  const filterInterior = useSelector(
    (state: any): InteriorFilterStateProps =>
      (state.pages.pcp as PCPCombinedState).filters.interior
  );

  const filterVehicleType = useSelector(
    (state: any): VehicleTypeFilterStateProps =>
      (state.pages.pcp as PCPCombinedState).filters.vehicleType
  );

  const filterEquipment = useSelector(
    (state: any): EquipmentFilterStateProps =>
      (state.pages.pcp as PCPCombinedState).filters.equipment
  );

  const filterLocation = useSelector(
    (state: any): LocationStateProps =>
      (state.pages.pcp as PCPCombinedState).filters.location
  );

  const filterMerchantLocation = useSelector(
    (state: any): MerchantLocationFilterStateProps =>
      (state.pages.pcp as PCPCombinedState).filters.merchantLocation
  );

  const filterPriceRange = useSelector((state: any): PriceFilterStateProps => {
    const f = (state.pages.pcp as PCPCombinedState).filters.priceRange;
    return f.key === '' ? defaultPriceRange : f;
  });

  const filterVehicleCondition = useSelector(
    (state: any): VehicleConditionStateProps =>
      (state.pages.pcp as PCPCombinedState).filters.vehicleCondition
  );

  const filterFuel = useSelector(
    (state: any): FuelFilterStateProps =>
      (state.pages.pcp as PCPCombinedState).filters.fuelType
  );

  const filterEmission = useSelector(
    (state: any): EmissionFilterStateProps =>
      (state.pages.pcp as PCPCombinedState).filters.emission
  );

  const filterAccordionOpenStates = useSelector(
    (state: any): PCPStateProps['filterAccordionStates'] =>
      state.pages.pcp.page.filterAccordionStates || []
  );

  const viewMode = useSelector(
    (state: any): PCPStateProps['viewMode'] =>
      (state.pages.pcp as PCPCombinedState).page.viewMode
  );

  const pageNumber = useSelector(
    (state: any): PCPStateProps['page'] =>
      (state.pages.pcp as PCPCombinedState).page.page
  );

  const itemsPerPage = useSelector(
    (state: any): PCPStateProps['itemsPerPage'] =>
      (state.pages.pcp as PCPCombinedState).page.itemsPerPage
  );

  const sorting = useSelector(
    (state: any): PCPStateProps['sorting'] =>
      (state.pages.pcp as PCPCombinedState).page.sorting
  );

  const previousRouteFilter = useSelector(
    (state: any): RouteFilter => state.pages.pcp.previousRouteFilter
  );

  return useMemo(
    () => ({
      filterMakeModel,
      filterBodyColors,
      filterDrive,
      filterEmission,
      filterEquipment,
      filterFuel,
      filterInterior,
      filterLocation,
      filterMerchantLocation,
      filterPriceRange,
      filterVehicleType,
      filterVehicleCondition,
      filterAccordionOpenStates,
      sorting,
      itemsPerPage,
      viewMode,
      pageNumber,
      previousRouteFilter,
    }),
    [
      filterAccordionOpenStates,
      filterBodyColors,
      filterDrive,
      filterEmission,
      filterEquipment,
      filterFuel,
      filterInterior,
      filterLocation,
      filterMerchantLocation,
      filterMakeModel,
      filterPriceRange,
      filterVehicleCondition,
      filterVehicleType,
      itemsPerPage,
      pageNumber,
      sorting,
      viewMode,
      previousRouteFilter,
    ]
  );
};
